// 첫 클리어 보상 대기 기록(PR #175 리뷰 P1): 장비·강화석은 장비 장부(gear.v1), 수령 기록은 진행 저장(stage.v1·stage.hard.v1)이라
// 서로 다른 키에 따로 저장된다. 장비 저장이 실패한 채 수령 기록만 남으면 재접속 때 보상이 영구히 사라지므로,
// 클리어 순간에는 "받을 보상(ops·골드·결과 영수증 id)"을 진행 저장에 함께 적어 두고(owed), 장비 장부 저장과 결과 영수증 저장이
// 모두 확인된 뒤에만 수령(claimed)으로 바꾼다. 재접속하면 owed의 연산을 같은 id로 다시 장부에 넣는다 — 장부가 연산 id·개체 uid로
// 중복을 거르므로 보상은 정확히 한 번만 들어간다. 이 필드는 선택 필드라 이전 빌드는 무시한다(깬 수로 수령 판정은 그대로).
// 메모리 안에서도 저장 형식(JSON)으로 들고 있어 진행 저장이 그대로 JSON.stringify 한다. 장부에 넣을 때 owedGearOps가 다시 검증해 푼다.
import { gearItemToJson, parseGearItem, type GearOp } from '../shared/gearLedger';

/** 저장 형식의 첫 클리어 연산(강화석·장비 줍기) */
export type OwedOp =
  | { readonly id: string; readonly t: 'stones'; readonly tier: 0 | 1 | 2 | 3; readonly n: number }
  | { readonly id: string; readonly t: 'pickup'; readonly item: Record<string, unknown> };

/** 첫 클리어 골드 보너스 영수증 id: 스테이지(하드는 h… id) 고정이라 어떤 경로로 클리어를 두 번 인정해도 골드는 한 번이다 */
export function firstGoldId(stage: string): string { return `first:${stage}`; }

export interface OwedReward {
  /** 장부 연산(강화석·장비). 고정 id(`fc:<스테이지 id>:…`)라 몇 번을 넣어도 한 번만 반영된다 */
  readonly ops: readonly OwedOp[];
  /** 결과 골드에 합쳐진 첫 클리어 골드(결과 영수증이 저장되지 않았을 때만 다시 준다) */
  readonly gold: number;
  /** 그 골드를 담은 보너스 영수증 id(firstGoldId) */
  readonly rid: string;
}

const MAX_OPS = 8;
const rec = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === 'object' && !Array.isArray(v);
const uint = (v: unknown, hi: number): v is number => typeof v === 'number' && Number.isSafeInteger(v) && v >= 0 && v <= hi;

/** 클리어 순간의 장부 연산 → 저장 형식 기록. 첫 클리어 연산(강화석·장비 줍기)이 아닌 연산은 버린다 */
export function makeOwed(ops: readonly GearOp[], gold: number, rid: string): OwedReward {
  const out: OwedOp[] = [];
  for (const op of ops) {
    if (op.t === 'stones') out.push({ id: op.id, t: 'stones', tier: op.tier as 0 | 1 | 2 | 3, n: op.n });
    else if (op.t === 'pickup') out.push({ id: op.id, t: 'pickup', item: gearItemToJson(op.item) });
  }
  return { ops: out, gold: Math.max(0, Math.round(gold)), rid };
}

/** 저장 원문 → 검증된 연산(틀리면 null). src·seq 같은 모르는 필드는 버린다 */
function parseOp(v: unknown): OwedOp | null {
  if (!rec(v) || typeof v.id !== 'string' || !v.id.startsWith('fc:') || v.id.length > 80) return null;
  if (v.t === 'stones') return uint(v.tier, 3) && uint(v.n, 9999) && v.n > 0 ? { id: v.id, t: 'stones', tier: v.tier as 0 | 1 | 2 | 3, n: v.n } : null;
  if (v.t === 'pickup') { const item = parseGearItem(v.item); return item ? { id: v.id, t: 'pickup', item: gearItemToJson(item) } : null; }
  return null;
}

export function parseOwed(v: unknown): OwedReward | null {
  if (!rec(v) || !Array.isArray(v.ops) || v.ops.length > MAX_OPS || !uint(v.gold, 1e9) || typeof v.rid !== 'string' || v.rid.length > 80) return null;
  const ops: OwedOp[] = [];
  for (const o of v.ops) { const op = parseOp(o); if (!op) return null; ops.push(op); }
  return { ops, gold: v.gold, rid: v.rid };
}

/** 저장 원문의 owed 맵 → 검증된 맵(틀린 항목·잘못된 키는 버린다) */
export function parseOwedMap(raw: unknown, validKey: (key: string) => boolean): Record<string, OwedReward> {
  const out: Record<string, OwedReward> = {};
  if (!rec(raw)) return out;
  for (const [k, v] of Object.entries(raw)) {
    if (!validKey(k)) continue;
    const o = parseOwed(v);
    if (o) out[k] = o;
  }
  return out;
}

/** 받는 중인 보상 → 장부에 넣을 연산(새 객체; 장부가 src·seq를 붙인다) */
export function owedGearOps(owed: OwedReward): GearOp[] {
  const out: GearOp[] = [];
  for (const op of owed.ops) {
    if (op.t === 'stones') out.push({ id: op.id, t: 'stones', tier: op.tier, n: op.n });
    else { const item = parseGearItem(op.item); if (item) out.push({ id: op.id, t: 'pickup', item }); }
  }
  return out;
}

/** 두 owed 합치기: 합집합, 단 수령(claimed)이 확정된 키는 뺀다(다른 탭이 이미 확정했으면 낡은 대기가 되살아나지 않게) */
export function mergeOwed(a: Readonly<Record<string, OwedReward>>, b: Readonly<Record<string, OwedReward>>, claimed: Readonly<Record<string, 1>>): Record<string, OwedReward> {
  const out: Record<string, OwedReward> = {};
  for (const src of [a, b]) for (const [k, v] of Object.entries(src)) if (claimed[k] !== 1) out[k] = v;
  return out;
}
